import { useEffect, useState, useRef, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import WagerPanel from "@/components/WagerPanel";
import { toast } from "sonner";
import { maybeCelebrate } from "@/lib/celebrate";
import { playSfx, startAmbient, stopAmbient } from "@/lib/sfx";
import { RotateCcw, ShieldCheck } from "lucide-react";

const AMBIENT_MAP = { chicken: "chicken", realtor: "realtor", jeff: "jeff", frogger: "frogger" };
const HIT_SFX = { chicken: "honk", realtor: "stamp", jeff: "splash", frogger: "squash" };

const RTP = 0.30;
const MAX_MULT = 10;
const stepMult = (p, step) => Math.min((1 / p) ** step * RTP, MAX_MULT);

/**
 * Generic path-crossing game (Chicken / Realtor / Jeff / Frogger etc.)
 * All theming is passed via props so future games are 1 file.
 *
 * Props:
 *  - slug: backend game key (must exist in PATH_GAMES on server)
 *  - title, headline: heading text
 *  - icon: JSX element for the pill
 *  - difficultyLabels: { easy, medium, hard, extreme }
 *  - actionVerb: e.g. "Cross", "Sell", "Swim", "Hop"
 *  - laneWord: e.g. "lane", "house", "current", "block"
 *  - playerSprite(size): JSX for the animated player sprite at given size
 *  - obstacleSprite(): JSX for an obstacle animating across the lane
 *  - laneVariant(index): optional per-lane background decoration
 *  - backgroundGradient: CSS for the track background
 *  - carLaneAnim: keyframe name for obstacle sweep
 *  - sceneOverlay: JSX rendered absolutely over the whole track (sky, water surface, etc.)
 *  - sceneFloor: JSX rendered absolutely across the bottom of the track (road, seabed, sidewalk)
 *  - obstacleKeyframes: custom CSS keyframes replacing the default fall animation
 *  - obstacleOnlyOnHit: when true, obstacle is only rendered when the step fails
 *  - preGameSlot: extra JSX rendered above the difficulty selector before a wager starts
 */
export default function PathGame({
  slug,
  title,
  headline,
  icon,
  actionVerb = "Cross",
  laneWord = "lane",
  playerSprite,
  obstacleSprite,
  laneVariant,
  backgroundGradient = "linear-gradient(180deg,#1B1F2A 0%,#0F131B 50%,#0A0D13 100%)",
  sceneOverlay = null,
  sceneFloor = null,
  obstacleKeyframes = null,
  minHeight = 220,
  preGameSlot = null,
  obstacleOnlyOnHit = false,
}) {
  const { refresh } = useAuth();
  const nav = useNavigate();
  const [configs, setConfigs] = useState([]);
  const [difficulty, setDifficulty] = useState("medium");
  const [autoMult, setAutoMult] = useState(0);
  const [autoLane, setAutoLane] = useState(0);
  const [clientSeed, setClientSeed] = useState("");
  const [session, setSession] = useState(null);
  const [seedHash, setSeedHash] = useState("");
  const [seedReveal, setSeedReveal] = useState("");
  const [step, setStep] = useState(0);
  const [hit, setHit] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [animating, setAnimating] = useState(false);
  const [carAt, setCarAt] = useState(null);
  const settleGuard = useRef(false);

  useEffect(() => {
    api.get(`/games/path/${slug}/config`).then((r) => setConfigs(r.data.difficulties));
    setClientSeed(Math.random().toString(36).slice(2, 12).toUpperCase());
    return () => { stopAmbient(); };
  }, [slug]);

  const cfg = configs.find((c) => c.id === difficulty);
  const p = cfg?.p ?? 0.85;
  const lanes = cfg?.lanes ?? 20;
  const currentMult = stepMult(p, step);
  const nextMult = stepMult(p, step + 1);
  const potential = session ? Math.floor(session.wager * currentMult) : 0;
  const nextPotential = session ? Math.floor(session.wager * nextMult) : 0;

  const settle = useCallback(async (cashedOut, stepsTaken, wasHit = false) => {
    if (settleGuard.current) return;
    settleGuard.current = true;
    setBusy(true);
    stopAmbient();
    try {
      const { data } = await api.post("/games/settle", {
        session_id: session.session_id,
        outcome: { steps: stepsTaken, difficulty, cashed_out: cashedOut && !wasHit, hit: !!wasHit },
      });
      setResult(data);
      setSeedReveal(data.server_seed_reveal || "");
      if (data.payout > 0) {
        toast.success(`+${data.payout.toLocaleString()} chips`);
        maybeCelebrate({ payout: data.payout, wager: session.wager });
        playSfx("cashOut");
        if (data.payout >= session.wager * 3) setTimeout(() => playSfx("megaWin"), 200);
      } else {
        toast.error(`No payout · lost ${session.wager}`);
        if (!wasHit) playSfx("lose");
      }
      await refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Settle failed"); }
    finally { setBusy(false); }
  }, [session, difficulty, refresh]);

  const start = async (wager) => {
    setBusy(true);
    try {
      const { data } = await api.post("/games/wager", {
        game: slug, wager,
        config: { difficulty, client_seed: clientSeed, auto_mult: autoMult, auto_lane: autoLane },
      });
      settleGuard.current = false;
      setSession(data);
      setSeedHash(data.server_seed_hash || "");
      setSeedReveal("");
      setStep(0); setHit(false); setResult(null); setCarAt(null);
      playSfx("wagerConfirm");
      if (AMBIENT_MAP[slug]) startAmbient(AMBIENT_MAP[slug]);
      await refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Cannot start"); }
    finally { setBusy(false); }
  };

  const takeStep = async () => {
    if (!session || result || hit || animating || busy) return;
    setAnimating(true);
    if (!obstacleOnlyOnHit) setCarAt(step);
    try {
      const { data } = await api.post("/games/path/step", { session_id: session.session_id });
      if (data.safe) {
        // Successful step — animate small delay for the number tick even without obstacle
        await new Promise((r) => setTimeout(r, obstacleOnlyOnHit ? 220 : 480));
        setStep(data.step);
        setCarAt(null);
        setAnimating(false);
        playSfx("step");
        if (data.reached_end) settle(true, data.step);
        else if (autoMult > 0 && data.mult >= autoMult) setTimeout(() => settle(true, data.step), 200);
        else if (autoLane > 0 && data.step >= autoLane) setTimeout(() => settle(true, data.step), 200);
      } else {
        // Failed step — always show the obstacle dropping, even in "only on hit" mode
        setCarAt(step);
        await new Promise((r) => setTimeout(r, 480));
        setHit(true); setAnimating(false);
        playSfx(HIT_SFX[slug] || "lose");
        setTimeout(() => settle(false, step, true), 400);
      }
    } catch (e) {
      setAnimating(false); setCarAt(null);
      toast.error(e.response?.data?.detail || "Step failed");
    }
  };

  const cashout = () => settle(true, step);
  const restart = () => {
    stopAmbient();
    setSession(null); setResult(null); setStep(0); setHit(false); setCarAt(null);
    setSeedHash(""); setSeedReveal("");
    setClientSeed(Math.random().toString(36).slice(2, 12).toUpperCase());
  };

  const copyProof = () => {
    navigator.clipboard.writeText(JSON.stringify({ server_seed_hash: seedHash, server_seed: seedReveal, client_seed: clientSeed }, null, 2));
    toast.success("Round proof copied");
  };

  return (
    <div className="space-y-6" data-testid={`${slug}-page`}>
      <div className="flex items-center justify-between">
        <div>
          <div className="pill pill-gold inline-flex items-center gap-2">{icon} {title}</div>
          <h1 className="font-display text-3xl mt-2">{headline}</h1>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => nav("/lobby")} className="btn-ghost text-sm" data-testid="back-to-lobby">Lobby</button>
        </div>
      </div>

      {!session ? (
        <div className="grid lg:grid-cols-2 gap-4">
          <div className="vexis-card p-5">
            <div className="pill">Difficulty</div>
            <div className="mt-3 grid grid-cols-2 gap-2">
              {configs.map((c) => (
                <button key={c.id} onClick={() => setDifficulty(c.id)} data-testid={`${slug}-diff-${c.id}`}
                  className={`text-left p-3 rounded-xl border transition ${difficulty === c.id ? "border-[color:var(--gold)] text-[color:var(--gold)] bg-[rgba(226,185,106,0.06)]" : "border-white/10 text-slate-300 hover:border-white/20"}`}>
                  <div className="text-sm capitalize">{c.id}</div>
                  <div className="text-xs font-mono text-slate-500 mt-1">1st step {c.first_step_mult.toFixed(2)}× · {c.lanes} {laneWord}s · max {MAX_MULT}×</div>
                </button>
              ))}
            </div>
            {preGameSlot && <div className="mt-4 pt-4 border-t border-white/5">{preGameSlot}</div>}
          </div>
          <div className="space-y-4">
            <WagerPanel game={title} onStart={start} busy={busy} />
            <div className="vexis-card p-4">
              <div className="pill">Auto-cashout</div>
              <div className="grid grid-cols-2 gap-3 mt-3">
                <div>
                  <label className="text-xs font-mono uppercase tracking-widest text-slate-500">At multiplier ≥</label>
                  <input type="number" step="0.1" min="0" max={MAX_MULT} value={autoMult} onChange={(e) => setAutoMult(parseFloat(e.target.value || "0"))} className="vexis mt-1" data-testid="auto-mult" placeholder="0 = off" />
                </div>
                <div>
                  <label className="text-xs font-mono uppercase tracking-widest text-slate-500">At {laneWord} ≥</label>
                  <input type="number" min="0" max={lanes} value={autoLane} onChange={(e) => setAutoLane(parseInt(e.target.value || "0"))} className="vexis mt-1" data-testid="auto-lane" placeholder="0 = off" />
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="grid lg:grid-cols-[1fr_320px] gap-6 items-start">
          <div className={`vexis-card p-4 sm:p-6 select-none relative overflow-hidden ${result ? (result.payout > 0 ? "win-glow" : "loss-glow") : ""}`}>
            <div className="flex items-center justify-between mb-3 text-xs font-mono text-slate-500 uppercase tracking-widest">
              <span>Start</span>
              <span>{laneWord} {step} / {lanes}</span>
              <span>Finish</span>
            </div>
            <div className="relative rounded-2xl overflow-hidden" style={{background: backgroundGradient}}>
              {sceneOverlay}
              <div className="grid relative" style={{ gridTemplateColumns: `54px repeat(${lanes}, minmax(30px, 1fr)) 54px`, minHeight }}>
                <div className="flex items-center justify-center relative" style={{background: "repeating-linear-gradient(45deg,#1F2937 0 10px,#141A24 10px 20px)"}}>
                  <div className="absolute inset-y-0 right-0 w-[3px] bg-[color:var(--gold)]/40" />
                  {step === 0 && !hit && !result && (
                    <span style={{ display: "inline-block", animation: "playerBob 1.4s ease-in-out infinite" }} data-testid={`${slug}-avatar-start`}>
                      {playerSprite ? playerSprite(36) : null}
                    </span>
                  )}
                </div>
                {Array.from({ length: lanes }).map((_, i) => {
                  const isCurrentPlayer = !result && i + 1 === step;
                  const isObstacleLane = carAt === i;
                  const passed = i < step;
                  const isHitLane = hit && isObstacleLane;
                  return (
                    <div key={i} className={`relative border-l border-white/[0.04] ${passed ? "bg-[rgba(16,185,129,0.05)]" : ""}`} data-testid={`lane-${i}`}>
                      {laneVariant ? laneVariant(i, passed) : (
                        <div className="absolute inset-x-0 top-1/2 h-[3px] -translate-y-1/2 opacity-40"
                          style={{background: "repeating-linear-gradient(90deg,#FFFFFF 0 8px,transparent 8px 20px)"}} />
                      )}
                      {isCurrentPlayer && (
                        <div className="absolute inset-0 flex items-center justify-center" style={{ zIndex: 1 }}>
                          <span style={{ display: "inline-block", animation: isHitLane ? "playerSquash 0.4s ease-in forwards" : "playerBob 1.4s ease-in-out infinite" }} data-testid={`${slug}-avatar`}>
                            {playerSprite ? playerSprite(30) : null}
                          </span>
                        </div>
                      )}
                      {isObstacleLane && (
                        <div className="absolute left-1/2 -translate-x-1/2" style={{ top: 0, animation: "obstaclePass .48s ease-out forwards", zIndex: 2 }}>
                          {obstacleSprite ? obstacleSprite() : null}
                        </div>
                      )}
                      {passed && (
                        <div className="absolute bottom-1 left-1/2 -translate-x-1/2 text-[10px] font-mono text-emerald-400/70">
                          {stepMult(p, i + 1).toFixed(2)}×
                        </div>
                      )}
                    </div>
                  );
                })}
                <div className="flex items-center justify-center relative" style={{background: "repeating-linear-gradient(45deg,#1F2937 0 10px,#141A24 10px 20px)"}}>
                  <div className="absolute inset-y-0 left-0 w-[3px] bg-emerald-400/50" />
                  {step >= lanes && !hit && playerSprite && playerSprite(36)}
                </div>
              </div>
              <style>{`
                @keyframes obstaclePass { 0% { transform: translate(-50%, -140%); } 100% { transform: translate(-50%, 260%); } }
                @keyframes playerBob { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-3px); } }
                @keyframes playerSquash { 0% { transform: scale(1) translateY(0); } 40% { transform: scale(1.15, 0.7) translateY(6px); filter: brightness(0.6) hue-rotate(-15deg); } 100% { transform: scale(1.05, 0.85) translateY(4px); filter: brightness(0.55); opacity: 0.85; } }
                ${obstacleKeyframes || ""}
              `}</style>
              {sceneFloor}
            </div>
          </div>

          <div className="space-y-3">
            <div className="vexis-card p-4">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><div className="text-slate-500 text-xs font-mono uppercase">Current</div><div className="font-display text-xl text-[color:var(--gold)]" data-testid={`${slug}-mult`}>{currentMult.toFixed(2)}×</div></div>
                <div><div className="text-slate-500 text-xs font-mono uppercase">Potential</div><div className="font-display text-xl" data-testid={`${slug}-potential`}>{potential.toLocaleString()}</div></div>
                <div><div className="text-slate-500 text-xs font-mono uppercase">Next</div><div className="font-display text-xl">{nextMult.toFixed(2)}×</div></div>
                <div><div className="text-slate-500 text-xs font-mono uppercase">Wager</div><div className="font-display text-xl">{session.wager}</div></div>
              </div>
              {(autoMult > 0 || autoLane > 0) && (
                <div className="mt-3 pt-3 border-t border-white/5 text-xs text-slate-400">
                  Auto: {autoMult > 0 && <>≥ <span className="text-[color:var(--gold)] font-mono">{autoMult}×</span></>}{autoMult > 0 && autoLane > 0 ? " or " : ""}{autoLane > 0 && <>{laneWord} <span className="text-[color:var(--gold)] font-mono">{autoLane}</span></>}
                </div>
              )}
            </div>

            {!result && (
              <>
                <button onClick={takeStep} disabled={hit || animating || busy || step >= lanes} className="btn-gold w-full inline-flex justify-center items-center gap-2" data-testid={`${slug}-step`}>
                  {animating ? `${actionVerb}…` : `${actionVerb} → ${nextPotential.toLocaleString()} chips`}
                </button>
                <button onClick={cashout} disabled={step === 0 || hit || animating || busy} className="btn-ghost w-full" data-testid={`${slug}-cashout`}>
                  Cash out · {potential.toLocaleString()} chips
                </button>
              </>
            )}

            {result && (
              <div className={`vexis-card p-4 ${result.payout > 0 ? "win-glow" : "loss-glow"}`} data-testid={`${slug}-result`}>
                {result.payout >= session.wager * 2 && (
                  <div className="pill pill-gold mb-2 inline-flex" data-testid={`${slug}-mega`}>✦ Mega Win · {(result.payout / session.wager).toFixed(2)}×</div>
                )}
                <div className={`font-display text-2xl ${result.payout > 0 ? "text-emerald-400" : "text-red-400"}`}>
                  {result.payout > 0 ? "Cashed out" : hit ? "Out" : "No payout"}
                </div>
                <div className="text-sm mt-1 text-slate-300">Payout · <span className="font-mono">{result.payout}</span> chips</div>
                <div className="mt-3 flex items-center gap-2 text-xs">
                  <button onClick={copyProof} className="btn-ghost text-xs inline-flex items-center gap-1" data-testid={`${slug}-verify`}>
                    <ShieldCheck className="w-3 h-3" /> Copy proof
                  </button>
                </div>
                <button onClick={restart} className="btn-gold w-full mt-4 inline-flex justify-center items-center gap-2" data-testid={`${slug}-play-again`}><RotateCcw className="w-4 h-4" /> Play again</button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
